// Which companies the signed-in user can open, and with which roles. Derived from `/v1/me` only:
// the app never joins across companies (spec 13 §Screens).
import type { CompanyRole, MeResponse } from "../api/types";

export type CompanyAccess = {
  id: string;
  slug: string;
  name: string;
  roles: CompanyRole[];
  /** The company's calendar (weeks, days, time zone) from `/v1/me` (S1-17 review #7). */
  calendar: MeResponse["memberships"][number]["company_calendar"];
  /** Reached through an agency grant (operator console user). */
  viaAgency: boolean;
  /**
   * FOUNDER-OS: `portfolio` for the caller's Founder OS company (`/v1/me` `portfolio`), which
   * shows the Portfolio mode; every other company is `operating`.
   */
  kind: "operating" | "portfolio";
};

/** FOUNDER-OS: whether a company is the caller's Founder OS company. */
export const kindOf = (me: MeResponse, companyId: string): CompanyAccess["kind"] =>
  me.portfolio?.company_id === companyId ? "portfolio" : "operating";

const MANAGER_ROLES: readonly CompanyRole[] = ["owner", "admin", "agency_operator"];

export function companiesOf(me: MeResponse): CompanyAccess[] {
  const byId = new Map<string, CompanyAccess>();
  for (const m of me.memberships) {
    byId.set(m.company_id, {
      id: m.company_id,
      slug: m.company_slug,
      name: m.company_name,
      calendar: m.company_calendar,
      roles: [...m.roles],
      viaAgency: false,
      kind: kindOf(me, m.company_id),
    });
  }
  for (const g of me.agency?.grants ?? []) {
    // An operator grant writes `company#agency_operator`; a viewer grant reads like a client.
    const role: CompanyRole = g.level === "operator" ? "agency_operator" : "client_viewer";
    const existing = byId.get(g.company_id);
    if (existing) {
      if (!existing.roles.includes(role)) existing.roles.push(role);
    } else {
      byId.set(g.company_id, {
        id: g.company_id,
        slug: g.company_slug,
        name: g.company_name,
        calendar: g.company_calendar,
        roles: [role],
        viaAgency: true,
        kind: kindOf(me, g.company_id),
      });
    }
  }
  return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
}

export function companyBySlug(me: MeResponse, slug: string): CompanyAccess | null {
  return companiesOf(me).find((c) => c.slug === slug) ?? null;
}

export function isManager(company: CompanyAccess): boolean {
  return company.roles.some((r) => MANAGER_ROLES.includes(r));
}

/**
 * `company#reader` (manager | client_viewer): reads the company's KPI, cost and report data. A
 * plain member (MEMBER-ROLE) is not one: it works in Chat and Work and reads the org chart.
 */
export function isReader(company: CompanyAccess): boolean {
  return company.roles.some((r) => MANAGER_ROLES.includes(r) || r === "client_viewer");
}

/** `company#member` (owner | admin | agency_operator | member; never a pure client viewer). */
export function isMember(company: CompanyAccess): boolean {
  return company.roles.some((r) => MANAGER_ROLES.includes(r) || r === "member");
}

/**
 * OPERATOR-ONLY-UX: the caller reaches this company only through its agency's operator grant,
 * with no person membership (an operator-managed company with no owner). Chat and Work writes and
 * the `my/*` reads need a person membership (the API answers 403), so the app shows a notice and
 * fires none of them.
 */
export function isOperatorOnly(company: CompanyAccess): boolean {
  return company.viaAgency && company.roles.includes("agency_operator");
}

/** `isOperatorOnly` for a company id of `/v1/me` (false when the company is not listed). */
export function operatorOnlyIn(me: MeResponse, companyId: string): boolean {
  if (me.memberships.some((m) => m.company_id === companyId)) return false;
  return (me.agency?.grants ?? []).some(
    (g) => g.company_id === companyId && g.level === "operator",
  );
}

export function isOperator(me: MeResponse): boolean {
  return me.agency !== null;
}
